import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, CircleUser, Plus, Search } from 'lucide-react'
import { cmsApi } from '../lib/api'
import type { ProducerCatalogItem, ProducerKind } from '../types'
import { KINDS } from './catalogForm'
import { CatalogDrafts } from './CatalogDrafts'
import { CatalogNotice } from './CatalogNotice'
import { LoadingState } from './LoadingState'

type Props = {
  canEdit: boolean
  catalogEditing: boolean
  onCreate: () => void
  onOpenProducer: (id: string) => void
  onOpenDraft: (id: string) => void
  onNotice: (message: string) => void
  onError: (message: string) => void
}

export function ProducersPage({ canEdit, catalogEditing, onCreate, onOpenProducer, onOpenDraft, onNotice, onError }: Props) {
  const [items, setItems] = useState<ProducerCatalogItem[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [kind, setKind] = useState<ProducerKind | ''>('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const pageSize = 24

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => setPage(1), [debouncedSearch, kind])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      if (debouncedSearch) params.set('search', debouncedSearch)
      if (kind) params.set('kind', kind)
      const result = await cmsApi.listProducers(params)
      setItems(result.items)
      setTotal(result.total)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar os perfis.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, kind, page])

  useEffect(() => { void load() }, [load])

  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="page-stack">
      <section className="page-heading page-heading--with-action">
        <div>
          <h1>Perfis</h1>
          <p>As páginas de produtores, locais e artistas no app: nome, @, foto e contatos.</p>
        </div>
        {canEdit && catalogEditing && <button className="button button--primary" onClick={onCreate}><Plus size={18} /> Novo perfil</button>}
      </section>

      <CatalogNotice catalogEditing={catalogEditing} />

      {canEdit && catalogEditing && <CatalogDrafts kind="producer" onOpen={onOpenDraft} onNotice={onNotice} onError={onError} />}

      <section className="panel panel--flush">
        <div className="filter-bar">
          <label className="search-field">
            <Search size={18} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome ou @" />
          </label>
          <select aria-label="Tipo" value={kind} onChange={(event) => setKind(event.target.value as ProducerKind | '')}>
            <option value="">Todos os tipos</option>
            {KINDS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
        </div>

        {loading ? <LoadingState label="Carregando perfis…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : items.length ? (
          <div className="producer-list">
            {items.map((producer) => (
              <button className="producer-row" key={producer.id} onClick={() => onOpenProducer(producer.id)}>
                <span className="producer-avatar">{producer.image_url ? <img src={producer.image_url} alt="" /> : (producer.name || '?').slice(0, 1).toUpperCase()}</span>
                <span className="producer-row__main"><strong>{producer.name}</strong><small>{producer.handle ? `@${producer.handle}` : 'Sem @'}</small></span>
                <span className="producer-row__pills">
                  <span className="status-pill status-pill--deleted">{KINDS.find((item) => item.value === producer.kind)?.label ?? 'Produtor'}</span>
                  {producer.has_upcoming_events && <span className="status-pill status-pill--published">Com eventos</span>}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="empty-state"><CircleUser size={30} /><h3>Nenhum perfil encontrado</h3><p>Revise a busca ou o tipo.</p></div>
        )}

        <div className="pagination">
          <span>{total.toLocaleString('pt-BR')} {total === 1 ? 'perfil' : 'perfis'}</span>
          <div>
            <button className="icon-button" aria-label="Página anterior" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={18} /></button>
            <span>{page} de {pageCount}</span>
            <button className="icon-button" aria-label="Próxima página" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>
    </div>
  )
}
